/**
 * The `?` prefix of the command palette (W2-14 onboarding-wizard; DESIGN.md §8.4 "`?` help topics and glossary"):
 * help topics of every module (title and article text searched) and the glossary words with their RPE and Chinese
 * community terms. A topic opens the Help panel at it; a glossary word opens the glossary article. Shortcuts in
 * articles read as the keys the active keymap binds now (markdownLite.tsx), so "F4" finds Publishing.
 */

import { DIALOG } from '../../app/dialogs/types';
import type { AppServices, Chord, PaletteItem, PaletteProvider } from '../../app/registry/types';
import { m } from './help.messages';
import { GLOSSARY, searchGlossary, searchTopics, TOPIC } from './helpTopics';
import { boundChord, displayChord, type KeyRef } from './keys';
import { plainText } from './markdownLite';

/** Characters of an article shown under its title. */
const EXCERPT = 90;

/** Most items the provider returns. */
export const MAX_ITEMS = 30;

export function helpPaletteProvider(
  services: Pick<AppServices, 'registry' | 'i18n' | 'ui' | 'keymap'>,
): PaletteProvider {
  const keys = (ref: KeyRef): Chord | null => {
    const chord = boundChord(services.keymap, ref);
    return chord === null ? null : displayChord(chord);
  };
  const label = (chord: Chord): string => {
    try {
      return services.keymap.label(chord);
    } catch {
      return chord;
    }
  };
  return {
    prefix: '?',
    search(query: string): readonly PaletteItem[] {
      const t = services.i18n;
      const topics = searchTopics(
        services.registry
          .helpTopics()
          .map((topic) => ({ topic, title: t.t(topic.title), body: plainText(t.t(topic.body), keys, label) })),
        query,
      );
      const items: PaletteItem[] = topics.map((x) => ({
        id: `help.topic.${x.topic.id}`,
        title: { key: x.topic.title },
        detail:
          x.body.length > EXCERPT
            ? { key: m.topicExcerpt, params: { excerpt: x.body.slice(0, EXCERPT).trimEnd() } }
            : { key: m.topicShort, params: { excerpt: x.body } },
        icon: 'BookOpen',
        run() {
          void services.ui.openDialog(DIALOG.help, { topic: x.topic.id });
        },
      }));
      const words = searchGlossary(
        GLOSSARY.map((entry) => ({ entry, term: t.t(entry.term), detail: t.t(entry.detail) })),
        query,
      );
      for (const w of words) {
        items.push({
          id: `help.glossary.${w.entry.id}`,
          title: { key: w.entry.term },
          detail: { key: w.entry.detail },
          icon: 'Languages',
          run() {
            void services.ui.openDialog(DIALOG.help, { topic: TOPIC.glossary });
          },
        });
      }
      return items.slice(0, MAX_ITEMS);
    },
  };
}
